import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail, formatDateTimeIT } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, User, Phone, Envelope, MapPin, Trash, PencilSimple, FloppyDisk, CaretLeft, CaretRight } from "@phosphor-icons/react";
import CustomerServicesSection from "@/components/CustomerServicesSection";
import CustomerRenewalsHistory from "@/components/CustomerRenewalsHistory";
import ManagedPlanSection from "@/components/ManagedPlanSection";
import RemoteMinutesSection from "@/components/RemoteMinutesSection";

export default function CustomerDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [items, setItems] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", surname: "", phone: "", phone2: "", email: "", email2: "", email3: "", pec: "", address: "", contratto: "", notes: "", ragione_sociale: "", piva: "", codice_fiscale: "", codice_sdi: "", iban: "", sede_legale: "" });
  const [intPage, setIntPage] = useState(1);
  const INT_PAGE_SIZE = 10;
  useDocumentTitle(c ? `${c.surname} ${c.name}` : "Cliente");

  const openEdit = () => {
    setForm({
      name: c.name || "",
      surname: c.surname || "",
      phone: c.phone || "",
      phone2: c.phone2 || "",
      email: c.email || "",
      email2: c.email2 || "",
      email3: c.email3 || "",
      pec: c.pec || "",
      address: c.address || "",
      contratto: c.contratto || "",
      notes: c.notes || "",
      ragione_sociale: c.ragione_sociale || "",
      piva: c.piva || "",
      codice_fiscale: c.codice_fiscale || "",
      codice_sdi: c.codice_sdi || "",
      iban: c.iban || "",
      sede_legale: c.sede_legale || "",
    });
    setEditing(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.surname.trim() || !form.phone.trim()) {
      return toast.error("Nome, cognome e telefono obbligatori");
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        surname: form.surname.trim(),
        phone: form.phone.trim(),
        phone2: form.phone2.trim() || null,
        email: form.email.trim() || null,
        email2: form.email2.trim() || null,
        email3: form.email3.trim() || null,
        pec: form.pec.trim() || null,
        address: form.address.trim() || null,
        contratto: form.contratto.trim() || null,
        notes: form.notes.trim() || null,
        ragione_sociale: form.ragione_sociale.trim() || null,
        piva: form.piva.trim() || null,
        codice_fiscale: form.codice_fiscale.trim() || null,
        codice_sdi: form.codice_sdi.trim() || null,
        iban: form.iban.trim() || null,
        sede_legale: form.sede_legale.trim() || null,
      };
      const { data } = await api.patch(`/customers/${id}`, payload);
      setC(data);
      // Refresh interventions to reflect denormalized fields
      const list = await api.get(`/customers/${id}/interventions`);
      setItems(list.data);
      toast.success("Cliente aggiornato");
      setEditing(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    (async () => {
      const [{ data: cust }, { data: list }] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/customers/${id}/interventions`),
      ]);
      setC(cust);
      setItems(list);
    })().catch(() => nav("/clienti"));
  }, [id, nav]);

  const remove = async () => {
    const msg = items.length > 0
      ? `Eliminando ${c.surname} ${c.name} verranno ELIMINATI anche ${items.length} interventi collegati. Sei sicuro?`
      : `Eliminare ${c.surname} ${c.name}?`;
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Cliente eliminato");
      nav("/clienti");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally {
      setDeleting(false);
    }
  };

  if (!c) return <div className="p-10 text-muted-foreground">Caricamento…</div>;

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto">
      <button onClick={() => nav(-1)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4"
              style={{ transition: "color 0.15s ease" }}>
        <ArrowLeft size={14} /> Indietro
      </button>

      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">// CLIENTE</div>
          <h1 className="text-4xl font-display font-bold tracking-tight" data-testid="customer-fullname">
            {c.surname} {c.name}
          </h1>
        </div>
        <Link to="/interventi/nuovo" state={{ customer_id: c.id }}>
          <Button className="rounded-full gap-2" data-testid="new-intervention-for-customer">
            <Plus size={16} weight="bold" /> Nuovo intervento
          </Button>
        </Link>
        <Button
          variant="outline"
          onClick={openEdit}
          data-testid="edit-customer-btn"
          className="rounded-full gap-2 ml-2"
        >
          <PencilSimple size={16} /> Modifica dati
        </Button>
        <Button
          variant="outline"
          onClick={remove}
          disabled={deleting}
          data-testid="delete-customer-btn"
          className="rounded-full gap-2 text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive ml-2"
        >
          <Trash size={16} /> {deleting ? "Elimino…" : "Elimina cliente"}
        </Button>
      </header>

      <div className="grid md:grid-cols-3 gap-6">
        <Card className="p-6 border-border/70 rounded-lg md:col-span-1">
          <h2 className="font-display font-bold tracking-tight text-lg mb-4">Contatti</h2>
          <div className="space-y-3 text-sm">
            <Row icon={Phone} label="Telefono" value={c.phone} mono />
            {c.phone2 && <Row icon={Phone} label="Telefono 2" value={c.phone2} mono />}
            {c.email && (
              <Row icon={Envelope} label="Email" value={
                <a href={`mailto:${c.email}`} className="text-primary hover:underline break-all" data-testid="customer-email-link">{c.email}</a>
              } />
            )}
            {c.email2 && (
              <Row icon={Envelope} label="Email 2" value={
                <a href={`mailto:${c.email2}`} className="text-primary hover:underline break-all">{c.email2}</a>
              } />
            )}
            {c.email3 && (
              <Row icon={Envelope} label="Email 3" value={
                <a href={`mailto:${c.email3}`} className="text-primary hover:underline break-all">{c.email3}</a>
              } />
            )}
            {c.pec && (
              <Row icon={Envelope} label="PEC" value={
                <a href={`mailto:${c.pec}`} className="text-primary hover:underline break-all" data-testid="customer-pec-link">{c.pec}</a>
              } />
            )}
            {c.address && <Row icon={MapPin} label="Indirizzo" value={c.address} />}

            {(c.ragione_sociale || c.piva || c.codice_fiscale || c.codice_sdi || c.iban || c.sede_legale) && (
              <div className="pt-3 mt-3 border-t border-border/50 space-y-2" data-testid="customer-billing-info">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Dati fatturazione</div>
                {c.ragione_sociale && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Ragione sociale</div>
                    <div className="mt-0.5">{c.ragione_sociale}</div>
                  </div>
                )}
                {c.piva && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">P.IVA</div>
                    <div className="mt-0.5 font-mono">{c.piva}</div>
                  </div>
                )}
                {c.codice_fiscale && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Codice fiscale</div>
                    <div className="mt-0.5 font-mono">{c.codice_fiscale}</div>
                  </div>
                )}
                {c.codice_sdi && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Codice SDI</div>
                    <div className="mt-0.5 font-mono">{c.codice_sdi}</div>
                  </div>
                )}
                {c.iban && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">IBAN</div>
                    <div className="mt-0.5 font-mono break-all">{c.iban}</div>
                  </div>
                )}
                {c.sede_legale && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Sede legale</div>
                    <div className="mt-0.5">{c.sede_legale}</div>
                  </div>
                )}
              </div>
            )}

            {c.contratto && (
              <div className="pt-3 mt-3 border-t border-border/50" data-testid="customer-contratto">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Contratto</div>
                <div className="mt-1 text-sm whitespace-pre-wrap">{c.contratto}</div>
              </div>
            )}
            {c.notes && (
              <div className="pt-3 mt-3 border-t border-border/50">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Note</div>
                <div className="mt-1 text-sm">{c.notes}</div>
              </div>
            )}
          </div>
        </Card>

        <div className="md:col-span-2 space-y-6">
          <ManagedPlanSection customerId={c.id} />
          <RemoteMinutesSection customerId={c.id} />

          <CustomerServicesSection customerId={c.id} customerEmail={c.email} customerSurname={c.surname} />

          <CustomerRenewalsHistory customerId={c.id} />

          <div>
          <h2 className="font-display font-bold tracking-tight text-lg mb-4">
            Storico interventi <span className="font-mono text-sm text-muted-foreground ml-2">({items.length})</span>
          </h2>
          {items.length === 0 ? (
            <Card className="p-8 border-border/70 rounded-lg text-center text-muted-foreground">
              Nessun intervento
            </Card>
          ) : (() => {
            const totalPages = Math.max(1, Math.ceil(items.length / INT_PAGE_SIZE));
            const currentPage = Math.min(intPage, totalPages);
            const startIdx = (currentPage - 1) * INT_PAGE_SIZE;
            const pageItems = items.slice(startIdx, startIdx + INT_PAGE_SIZE);
            return (
              <>
                <div className="space-y-3">
                  {pageItems.map((it) => (
                    <Link
                      key={it.id}
                      to={`/interventi/${it.id}`}
                      data-testid={`customer-intervention-${it.code}`}
                    >
                      <Card className="p-4 border-border/70 rounded-lg hover:shadow-md hover:-translate-y-0.5"
                            style={{ transition: "transform 0.2s ease, box-shadow 0.2s ease" }}>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-mono text-xs text-muted-foreground">{it.code}</div>
                            <div className="font-medium mt-1">
                              {it.product_type} — {it.work_description.slice(0, 60)}{it.work_description.length > 60 ? "…" : ""}
                            </div>
                            <div className="font-mono text-xs text-muted-foreground mt-1">
                              {new Date(it.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                              {it.closed_at && <> · chiuso {new Date(it.closed_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</>}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            {it.type === "home" && (
                              <Badge className="bg-primary/15 text-primary border border-primary/30 hover:bg-primary/15 font-mono text-[10px] uppercase">
                                Domicilio
                              </Badge>
                            )}
                            <Badge className={`font-mono text-[10px] uppercase border ${
                              it.status === "open"
                                ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 hover:bg-amber-500/15"
                                : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/15"
                            }`}>{it.status === "open" ? "Aperto" : "Chiuso"}</Badge>
                          </div>
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
                {totalPages > 1 && (
                  <div className="mt-3 flex items-center justify-between" data-testid="interventions-pagination">
                    <div className="font-mono text-[10px] text-muted-foreground">
                      Pagina {currentPage} di {totalPages} · {startIdx + 1}-{Math.min(startIdx + INT_PAGE_SIZE, items.length)} di {items.length}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIntPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        data-testid="int-page-prev"
                        className="h-7 w-7 p-0 rounded"
                      ><CaretLeft size={13} /></Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIntPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        data-testid="int-page-next"
                        className="h-7 w-7 p-0 rounded"
                      ><CaretRight size={13} /></Button>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
          </div>
        </div>
      </div>

      {/* Edit customer dialog */}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica dati cliente</DialogTitle>
            <DialogDescription>Aggiorna i dati anagrafici e di contatto.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3" data-testid="edit-customer-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Cognome *</Label>
                <Input required value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} data-testid="edit-customer-surname" className="mt-1.5" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Nome *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-customer-name" className="mt-1.5" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Telefono *</Label>
                <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-customer-phone" className="mt-1.5" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Telefono 2</Label>
                <Input value={form.phone2} onChange={(e) => setForm({ ...form, phone2: e.target.value })} data-testid="edit-customer-phone2" className="mt-1.5" placeholder="Opzionale" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-customer-email" className="mt-1.5" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Email 2</Label>
                <Input type="email" value={form.email2} onChange={(e) => setForm({ ...form, email2: e.target.value })} data-testid="edit-customer-email2" className="mt-1.5" placeholder="Opzionale" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Email 3</Label>
                <Input type="email" value={form.email3} onChange={(e) => setForm({ ...form, email3: e.target.value })} data-testid="edit-customer-email3" className="mt-1.5" placeholder="Opzionale" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">PEC</Label>
                <Input type="email" value={form.pec} onChange={(e) => setForm({ ...form, pec: e.target.value })} data-testid="edit-customer-pec" className="mt-1.5" placeholder="Opzionale" />
              </div>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Indirizzo</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="edit-customer-address" className="mt-1.5" />
            </div>

            <div className="border-t border-border/50 pt-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">// Dati fatturazione (opzionali)</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="font-mono text-[10px] uppercase tracking-widest">Ragione sociale</Label>
                  <Input value={form.ragione_sociale} onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })} data-testid="edit-customer-ragione" className="mt-1.5" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest">P.IVA</Label>
                  <Input value={form.piva} onChange={(e) => setForm({ ...form, piva: e.target.value })} data-testid="edit-customer-piva" className="mt-1.5 font-mono" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest">Codice Fiscale</Label>
                  <Input value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} data-testid="edit-customer-cf" className="mt-1.5 font-mono" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest">Codice SDI</Label>
                  <Input value={form.codice_sdi} onChange={(e) => setForm({ ...form, codice_sdi: e.target.value })} data-testid="edit-customer-sdi" className="mt-1.5 font-mono" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest">IBAN</Label>
                  <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} data-testid="edit-customer-iban" className="mt-1.5 font-mono" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="font-mono text-[10px] uppercase tracking-widest">Sede legale</Label>
                  <Input value={form.sede_legale} onChange={(e) => setForm({ ...form, sede_legale: e.target.value })} data-testid="edit-customer-sede" className="mt-1.5" />
                </div>
              </div>
            </div>

            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Contratto <span className="text-muted-foreground/70 normal-case">(opzionale)</span></Label>
              <Textarea rows={2} placeholder="Es. Contratto Assistenza n. 2026/001 · durata annuale · rinnovo tacito" value={form.contratto} onChange={(e) => setForm({ ...form, contratto: e.target.value })} data-testid="edit-customer-contratto" className="mt-1.5" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Note</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="edit-customer-notes" className="mt-1.5" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setEditing(false)}>Annulla</Button>
              <Button type="submit" disabled={saving} data-testid="edit-customer-save" className="rounded-full gap-2">
                <FloppyDisk size={16} weight="bold" /> {saving ? "Salvo…" : "Salva modifiche"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ icon: Icon, label, value, mono }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        <Icon size={12} /> {label}
      </div>
      <div className={`mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
